/**
 * Time zone aware display. The API stores and returns instants in UTC (ISO 8601 with "Z");
 * everything shown to a person is converted to *their* zone: the zone saved on their profile,
 * otherwise the browser's. Calendar-only values (YYYY-MM-DD, such as a quote's validity date)
 * are not instants and are never shifted.
 */

export interface DisplayPrefs { timeZone: string; locale: string | undefined }

/** Browsers report some zones by legacy CLDR names (Chrome: "Asia/Calcutta"); the server stores
 * current IANA names, so map them before comparing or saving. Mirrors backend LEGACY_ZONES. */
const LEGACY: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata", "Asia/Katmandu": "Asia/Kathmandu", "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Rangoon": "Asia/Yangon", "Asia/Dacca": "Asia/Dhaka", "Asia/Thimbu": "Asia/Thimphu",
  "Asia/Ujung_Pandang": "Asia/Makassar", "Europe/Kiev": "Europe/Kyiv", "Atlantic/Faeroe": "Atlantic/Faroe",
  "America/Godthab": "America/Nuuk", "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca", "America/Cordoba": "America/Argentina/Cordoba",
  "America/Jujuy": "America/Argentina/Jujuy", "America/Mendoza": "America/Argentina/Mendoza",
  "America/Indianapolis": "America/Indiana/Indianapolis", "America/Louisville": "America/Kentucky/Louisville",
  "Africa/Asmera": "Africa/Asmara", "Pacific/Truk": "Pacific/Chuuk", "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Enderbury": "Pacific/Kanton", "Etc/UTC": "UTC", "Etc/GMT": "UTC", GMT: "UTC",
};
export const canonicalZone = (zone: string): string => LEGACY[zone] ?? zone;

export const browserZone = (): string => canonicalZone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

let prefs: DisplayPrefs = { timeZone: browserZone(), locale: undefined };

export function setDisplayPrefs(next: { timeZone?: string | null; locale?: string | null }): void {
  prefs = { timeZone: next.timeZone || browserZone(), locale: next.locale || undefined };
}
export const displayPrefs = (): DisplayPrefs => prefs;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "12 Oct 2026, 3:45 pm" in the user's zone; ``zone`` appends "IST", "GMT+5:30"… */
export function formatDateTime(iso: string, opts: { zone?: boolean } = {}): string {
  const at = new Date(iso);
  const text = new Intl.DateTimeFormat(prefs.locale, { dateStyle: "medium", timeStyle: "short", timeZone: prefs.timeZone }).format(at);
  return opts.zone ? `${text} ${zoneLabel(at)}` : text; // dateStyle cannot be combined with timeZoneName
}

/** "12 Oct 2026". Instants are converted to the user's zone; plain dates are shown as is. */
export function formatDay(iso: string | null): string {
  if (!iso) return "";
  const dateOnly = DATE_ONLY.test(iso);
  return new Intl.DateTimeFormat(prefs.locale, {
    day: "numeric", month: "short", year: "numeric",
    timeZone: dateOnly ? "UTC" : prefs.timeZone,
  }).format(new Date(dateOnly ? `${iso}T00:00:00Z` : iso));
}

/** Short zone name for the user's zone, e.g. "IST" or "GMT+5:30". */
export function zoneLabel(at: Date = new Date(), timeZone = prefs.timeZone): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
    .formatToParts(at).find((p) => p.type === "timeZoneName");
  return part?.value ?? timeZone;
}

function wallClock(at: Date, timeZone: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(at);
  const out: Record<string, number> = {};
  for (const p of parts) if (p.type !== "literal") out[p.type] = Number(p.value);
  return out;
}

/** Offset of ``timeZone`` from UTC at instant ``at``, in milliseconds. */
export function zoneOffsetMs(at: Date, timeZone: string): number {
  const w = wallClock(at, timeZone);
  const asUtc = Date.UTC(w.year!, w.month! - 1, w.day!, w.hour!, w.minute!, w.second!);
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/** Value for a datetime-local input showing ``d`` in the user's zone. */
export function toLocalInput(d: Date, timeZone = prefs.timeZone): string {
  const w = wallClock(d, timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${w.year}-${pad(w.month!)}-${pad(w.day!)}T${pad(w.hour!)}:${pad(w.minute!)}`;
}

/** datetime-local value, read as wall-clock time in the user's zone, to a UTC ISO string. */
export function fromLocalInput(value: string, timeZone = prefs.timeZone): string {
  const [date, time = "00:00"] = value.split("T");
  const [y, m, d] = date!.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y!, m! - 1, d!, hh!, mm!);
  // Two passes settle the offset across DST changes.
  let utc = guess - zoneOffsetMs(new Date(guess), timeZone);
  utc = guess - zoneOffsetMs(new Date(utc), timeZone);
  return new Date(utc).toISOString();
}

/** Every IANA zone the browser knows, for the profile picker. */
export function allZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const zones = new Set((intl.supportedValuesOf?.("timeZone") ?? []).map(canonicalZone));
  zones.add(browserZone());
  zones.delete("UTC");
  return ["UTC", ...[...zones].sort()];
}
